import type { SxProps, Theme } from "@mui/material";
import { Box, Typography, useTheme } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CalendarIcon } from "components/icons";
import { useGlobalStore } from "controller/store/globalStore";
import type { Dayjs } from "dayjs";
import { dayjs } from "utilz";

interface DateTimeInputProps {
  sx?: SxProps<Theme>;
  value: Dayjs | string | null;
  label?: string;
  /**
   * Mark the field mandatory — appends " *" to the label, matching how `Input`
   * and `SelectField` render their required marker. Purely presentational: the
   * actual enforcement stays in the form resolver.
   */
  required?: boolean;
  onChangeHandler: (value: Dayjs | null | string) => void;
  size?: "small" | "large" | "medium";
  disabled?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  minDate?: Dayjs | string | null;
  minTime?: Dayjs | string | null;
  minDateTime?: Dayjs | string | null;
  maxDate?: Dayjs | string | null;
  maxDateTime?: Dayjs | string | null;

  error?: boolean;
  errorMessage?: string;
  timezone?: string;
  fullWidth?: boolean;
  hideHelperBox?: boolean;
  backgourndColor?: string;
  disableOpenPicker?: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: library requirement
  slots?: Record<string, any>;
  // biome-ignore lint/suspicious/noExplicitAny: library requirement
  slotProps?: Record<string, any>;
  overideValidationError?: boolean;
  /**
   * Reports the picker's own validation result on every change — `null` when the
   * value is good, otherwise MUI's error key ("invalidDate", "minDate", …).
   *
   * Needed because an invalid value is deliberately NOT forwarded to
   * `onChangeHandler` below, so without this the host has no way to know the
   * field is broken: it keeps showing the half-typed text while the form still
   * holds the previous value, and says nothing (AAC-585).
   */
  onValidationError?: (error: string | null) => void;
}

const EmptyIcon = () => <Box sx={{ width: "24px", height: "24px" }} />;
/**
 * Required marker for the picker's label. Mirrors `Input` / `SelectField`, which
 * both append " *" to a required field's label. Kept at module scope so the
 * branch stays out of the component body.
 */
const withRequiredMarker = (
  label: string | undefined,
  required: boolean,
): string | undefined => (required && label ? `${label} *` : label);

export const DateTimeInput = ({
  sx,
  value,
  onChangeHandler,
  label,
  required = false,
  size = "small",
  disabled = false,
  disableFuture = false,
  disablePast = false,
  minDate,
  error,
  minTime,
  errorMessage,
  minDateTime,
  maxDate,
  maxDateTime,
  timezone,
  fullWidth = false,
  hideHelperBox = true,
  backgourndColor,
  disableOpenPicker = false,
  slots,
  slotProps,
  overideValidationError = false,
  onValidationError,
}: DateTimeInputProps) => {
  const theme = useTheme();
  const dateFormat =
    useGlobalStore((state) => state.dateFormat) || "MM/DD/YYYY";
  const displayLabel = withRequiredMarker(label, required);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateTimePicker
        disableOpenPicker={disableOpenPicker}
        value={value}
        disableFuture={disableFuture}
        disablePast={disablePast}
        disabled={disabled}
        minDate={minDate ? dayjs(minDate) : undefined}
        minTime={minTime ? dayjs(minTime) : undefined}
        minDateTime={minDateTime ? dayjs(minDateTime) : undefined}
        maxDate={maxDate ? dayjs(maxDate) : undefined}
        maxDateTime={maxDateTime ? dayjs(maxDateTime) : undefined}
        // timezone={timezone}
        closeOnSelect={true}
        ampm={true}
        ampmInClock
        label={displayLabel}
        format={`${dateFormat} h:mm a`}
        timezone={timezone}
        onChange={(newValue, context) => {
          const validationError = context?.validationError ?? null;
          onValidationError?.(validationError);
          if (validationError == null || overideValidationError) {
            onChangeHandler(newValue);
          }
        }}
        // onAccept={(newValue) => onChangeHandler(newValue)}
        slots={{
          openPickerIcon: disabled ? EmptyIcon : CalendarIcon,

          ...slots,
        }}
        slotProps={{
          openPickerButton: {
            color: error ? "error" : "inherit",
          },
          actionBar: {
            // actions: ["accept"] as PickersActionBarAction[],
            sx: {
              "&.MuiDialogActions-root": {
                color: `${theme.palette.purple.main} !important`,
                "& .MuiButtonBase-root": {
                  ...theme.typography.caption,
                  color: `${theme.palette.purple.main} !important`,
                },
              },
            },
          },
          desktopPaper: {
            sx: {
              "& .MuiPickersDay-root": {
                borderRadius: "6px",
                ":hover": {
                  bgcolor: theme.palette.neutral["005"],
                },
                ...theme.typography["body-small"],
                "&.Mui-selected": {
                  backgroundColor: theme.palette.purple.main,
                  color: "#FFF",
                },
                "&.MuiPickersDay-today": {
                  border: `1px solid ${theme.palette.purple.main}`,
                },
              },
              "& .MuiMultiSectionDigitalClock-root": {
                ...theme.typography["body-medium"],
                "& .Mui-selected": {
                  backgroundColor: "#EBFCFE",
                  border: `1px solid ${theme.palette.purple.main}`,
                  color: theme.palette.purple.main,
                  borderRadius: "6px",
                },
              },
              borderRadius: "16px",
              // // biome-ignore lint/suspicious/noDuplicateObjectKeys: MUI fallback shadows
              // boxShadow: "0px 5px 5px -3px rgba(0, 0, 0, 0.2)",
              // // biome-ignore lint/suspicious/noDuplicateObjectKeys: MUI fallback shadows
              // boxShadow: "0px 8px 10px 1px rgba(0, 0, 0, 0.14)",
              boxShadow: "0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
            },
          },
          textField: {
            helperText: (
              <Box
                sx={{
                  mt: errorMessage ? "4px" : hideHelperBox ? "0px" : "4px",
                }}
              >
                <Typography variant="body-small" color="error">
                  {errorMessage}
                </Typography>
              </Box>
            ),
            error: error ? true : false,
            size: size,
            fullWidth: fullWidth,
            // InputProps: {
            //   endAdornment: <CustomInputAdornment position="end" />,
            // },
            // InputProps: {
            //   endAdornment: <CalendarIcon />,
            // },
            sx: {
              ...theme.typography["body-medium"],
              "& .MuiOutlinedInput-root": {
                height: { sm: "32px" },
                backgroundColor: backgourndColor
                  ? backgourndColor
                  : error
                    ? theme.palette.error.tint
                    : "white",
                "& > fieldset": {
                  borderColor: theme.palette.neutral["020"],
                  borderRadius: "4px",
                },
                "& input::placeholder": { fontSize: "14px" },
              },
              "& .MuiOutlinedInput-root.Mui-focused": {
                color: theme.palette.lightBg.high,
                "& > fieldset": { borderColor: theme.palette.purple.main },
              },
              "& label": {
                "&.Mui-focused": { color: theme.palette.purple.main },
              },
              "& .MuiFormHelperText-root": {
                mt: "0px",
              },
              ...sx,
            },
          },

          ...slotProps,
        }}
      />
    </LocalizationProvider>
  );
};
